import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import pediatricIntakeSchema from "@/data/pediatric_intake_schema.json";
import {
  ageDaysFromDob,
  evaluateShowIf,
  serializeIntakeValue,
  validateFieldsRecursive,
  type IntakeFieldDef,
  type ShowIf,
} from "@/utils/intakeHelpers";

export type IntakeSectionDef = {
  section_id: string;
  title: string;
  note?: string;
  show_if?: ShowIf;
  fields: IntakeFieldDef[];
};

export type IntakePageDef = {
  page_id: string;
  title: string;
  note?: string;
  show_for_age_days: { min: number; max: number | null };
  sections: IntakeSectionDef[];
};

const schemaPages = pediatricIntakeSchema.pages as IntakePageDef[];

function filterVisiblePages(
  pages: IntakePageDef[],
  ageDays: number | null
): IntakePageDef[] {
  if (ageDays === null) {
    return pages.filter((p) => p.page_id === "cover");
  }
  return pages.filter((p) => {
    const { min, max } = p.show_for_age_days;
    return ageDays >= min && (max === null || ageDays <= max);
  });
}

function draftStorageKey(caseHistoryId: number, patientKey: string): string {
  return `pediatric_intake_draft_${patientKey}_${caseHistoryId}`;
}

export function useIntakeForm(
  caseHistoryId: number,
  patientKey: string,
  initialSeed: Record<string, unknown>
) {
  const [values, setValues] = useState<Record<string, unknown>>(() => ({
    ...initialSeed,
  }));
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [draftPrompt, setDraftPrompt] = useState<"ask" | "resolved">("resolved");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const childDob = values.child_dob;
  const ageDays = useMemo(() => {
    if (typeof childDob !== "string" || !childDob.trim()) return null;
    return ageDaysFromDob(childDob);
  }, [childDob]);

  const visiblePages = useMemo(
    () => filterVisiblePages(schemaPages, ageDays),
    [ageDays]
  );

  useEffect(() => {
    setCurrentPageIndex((i) =>
      Math.min(i, Math.max(0, visiblePages.length - 1))
    );
  }, [visiblePages.length]);

  const setFieldValue = useCallback((id: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  useEffect(() => {
    if (!caseHistoryId || !patientKey) {
      setDraftPrompt("resolved");
      return;
    }
    const key = draftStorageKey(caseHistoryId, patientKey);
    try {
      const raw = localStorage.getItem(key);
      setDraftPrompt(raw ? "ask" : "resolved");
    } catch {
      setDraftPrompt("resolved");
    }
  }, [caseHistoryId, patientKey]);

  useEffect(() => {
    if (draftPrompt !== "resolved" || !caseHistoryId || !patientKey) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      try {
        const key = draftStorageKey(caseHistoryId, patientKey);
        localStorage.setItem(key, JSON.stringify(values));
      } catch {
        /* ignore */
      }
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [values, caseHistoryId, patientKey, draftPrompt]);

  const continueDraft = useCallback(() => {
    if (!caseHistoryId || !patientKey) return;
    try {
      const key = draftStorageKey(caseHistoryId, patientKey);
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        setValues((prev) => ({ ...prev, ...parsed }));
      }
    } catch {
      /* ignore */
    }
    setDraftPrompt("resolved");
  }, [caseHistoryId, patientKey]);

  const discardDraft = useCallback(() => {
    if (!caseHistoryId || !patientKey) return;
    try {
      localStorage.removeItem(draftStorageKey(caseHistoryId, patientKey));
    } catch {
      /* ignore */
    }
    setValues(() => ({ ...initialSeed }));
    setDraftPrompt("resolved");
  }, [caseHistoryId, patientKey, initialSeed]);

  const validateCurrentPage = useCallback((): boolean => {
    const page = visiblePages[currentPageIndex];
    if (!page) return true;
    const nextErrors: Record<string, string> = {};
    for (const sec of page.sections) {
      if (!evaluateShowIf(sec.show_if, values, ageDays)) continue;
      validateFieldsRecursive(sec.fields, values, ageDays, nextErrors);
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [visiblePages, currentPageIndex, values, ageDays]);

  const validateAllVisible = useCallback((): boolean => {
    const nextErrors: Record<string, string> = {};
    for (const page of visiblePages) {
      for (const sec of page.sections) {
        if (!evaluateShowIf(sec.show_if, values, ageDays)) continue;
        validateFieldsRecursive(sec.fields, values, ageDays, nextErrors);
      }
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [visiblePages, values, ageDays]);

  const goNext = useCallback(() => {
    if (!validateCurrentPage()) return false;
    setCurrentPageIndex((i) =>
      Math.min(i + 1, Math.max(0, visiblePages.length - 1))
    );
    return true;
  }, [validateCurrentPage, visiblePages.length]);

  const goPrev = useCallback(() => {
    setCurrentPageIndex((i) => Math.max(0, i - 1));
    setErrors({});
  }, []);

  const isLastPage = currentPageIndex >= visiblePages.length - 1;

  const clearDraftPersisted = useCallback(() => {
    if (!caseHistoryId || !patientKey) return;
    try {
      localStorage.removeItem(draftStorageKey(caseHistoryId, patientKey));
    } catch {
      /* ignore */
    }
  }, [caseHistoryId, patientKey]);

  return {
    schemaMeta: pediatricIntakeSchema._meta,
    visiblePages,
    currentPageIndex,
    currentPage: visiblePages[currentPageIndex],
    values,
    setFieldValue,
    ageDays,
    errors,
    isSubmitting,
    setIsSubmitting,
    submissionComplete,
    setSubmissionComplete,
    submissionError,
    setSubmissionError,
    goNext,
    goPrev,
    isLastPage,
    validateCurrentPage,
    validateAllVisible,
    draftPrompt,
    setDraftPrompt,
    continueDraft,
    discardDraft,
    clearDraftPersisted,
    serializeValue: serializeIntakeValue,
  };
}

export { schemaPages };
